import type { HomeType, PropertyState } from "./types.js";

/**
 * Homes, and the rules for getting one.
 *
 * A member has exactly one home. The three types below are unlocked together by finishing the
 * first quest, and the first pick is free — that choice is the reward. Moving into a different
 * one afterwards costs a change fee, whichever unlocked type you move to. Later home types will
 * unlock by purchase; the fee to move in stays the same, so this rule does not need revisiting
 * when the catalogue grows.
 */

export const HOME_TYPES = [
  {
    type: "hobbit_hole",
    itemId: "home-hobbit-hole",
    name: "Hobbit Hole",
    description: "A round door set into a grassy bank, with the roof planted over."
  },
  {
    type: "tudor_cottage",
    itemId: "home-tudor-cottage",
    name: "Tudor Cottage",
    description: "Whitewashed panels between dark timber, under a steep shingled roof."
  },
  {
    type: "cozy_cabin",
    itemId: "home-cozy-cabin",
    name: "Cozy Cabin",
    description: "Stacked timber walls and a deep porch, warm from the chimney out."
  }
] as const satisfies ReadonlyArray<{ type: HomeType; itemId: string; name: string; description: string }>;

export type HomeTypeInfo = (typeof HOME_TYPES)[number];

/** What it costs to move into a different home once you already have one. */
export const HOME_CHANGE_FEE_COPPER = 250;

/** The starter set, granted together when the first quest is completed. */
export const STARTER_HOME_ITEM_IDS: string[] = HOME_TYPES.map((home) => home.itemId);

export function getHomeType(type: HomeType): HomeTypeInfo {
  const found = HOME_TYPES.find((home) => home.type === type);
  if (!found) throw new Error(`Unknown home type ${type}`);
  return found;
}

export function homeItemId(type: HomeType): string {
  return getHomeType(type).itemId;
}

export type HomeChoiceResult =
  | { ok: true; costCopper: number; isFirstHome: boolean }
  | { ok: false; message: string };

/**
 * The single rule for taking a home, shared by the activity and the worker so an optimistic
 * update can never disagree with what the server will allow.
 */
export function evaluateHomeChoice(input: {
  property: Pick<PropertyState, "homeType">;
  ownedItemIds: string[];
  walletCopper: number;
  type: HomeType;
}): HomeChoiceResult {
  const home = HOME_TYPES.find((candidate) => candidate.type === input.type);
  if (!home) return { ok: false, message: "That home does not exist" };
  if (!input.ownedItemIds.includes(home.itemId)) {
    return { ok: false, message: `${home.name} is not unlocked yet` };
  }
  if (input.property.homeType === input.type) {
    return { ok: false, message: `You already live in the ${home.name}` };
  }

  const isFirstHome = input.property.homeType === null;
  const costCopper = isFirstHome ? 0 : HOME_CHANGE_FEE_COPPER;
  if (input.walletCopper < costCopper) {
    return { ok: false, message: "You do not have enough coins for the change fee" };
  }
  return { ok: true, costCopper, isFirstHome };
}
